import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVLoader, SVButton, SVForm, SVAutocomplete } from '../../core';
import { useForm } from 'react-hook-form';
import {
  useDeactivateTeammate,
  useGetTeammateListForSelection,
} from '@/hooks/requests/useTeammates';
import { StatusQueryParam } from '@/types/common';
import { TeammateRole } from '@/types/teammates';

type FormData = {
  teammateId: string;
};

type TeammateDeactivateModalProps = {
  id: string;
  companyId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function TeammateDeactivateModal({
  id,
  companyId,
  open,
  setOpen,
}: TeammateDeactivateModalProps) {
  const {
    canDeactivateTeammate,
    isCanDeactivateTeammatePending,
    deactivateTeammate,
    isDeactivateTeammatePending,
  } = useDeactivateTeammate(id, open);

  const formMethods = useForm<FormData>({
    defaultValues: {
      teammateId: '',
    },
    mode: 'onBlur',
  });
  const { teammatesList } = useGetTeammateListForSelection(
    {
      company_id: companyId,
      status: StatusQueryParam.ACTIVE,
      roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR],
    },
    open,
  );

  if (isCanDeactivateTeammatePending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  async function handleTeammateSwapDeactivate(data: FormData) {
    try {
      await deactivateTeammate(data.teammateId);
      showToast({
        type: 'success',
        data: { content: 'Teammate has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  if (!canDeactivateTeammate) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <SVForm methods={formMethods} onSubmit={handleTeammateSwapDeactivate}>
          <DialogContent>
            <Typography variant="h4">Cannot Deactive Teammate</Typography>
            <Typography mb={2}>
              This teammate is listed as the advisor for active customers,
              businesses, bank accounts, bank applications and/or pending
              transaction requests. Select a new advisor within your company for
              which to transfer ownership.
            </Typography>
            <SVAutocomplete
              label="Advisor"
              name="teammateId"
              options={teammatesList}
              rules={{ required: true }}
            />
          </DialogContent>
          <DialogActions>
            <SVButton variant="outlined" onClick={() => setOpen(false)}>
              Back
            </SVButton>
            <SVButton
              type="submit"
              onClick={formMethods.handleSubmit(handleTeammateSwapDeactivate)}>
              Confirm
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    );
  }

  async function handleDeactivate() {
    try {
      await deactivateTeammate('');
      showToast({
        type: 'success',
        data: { content: 'Teammate has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Deactivate Teammate?</Typography>
        <Typography>
          Are you sure you would like to deactivate this teammate?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Deactivate
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isDeactivateTeammatePending}
          onClick={handleDeactivate}>
          Yes, Deactivate
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
